import { useEffect, useRef } from "react";

import { PaywallContactDialog } from "~/components/billing/paywall-contact-dialog";
import { trackEvent } from "~/lib/analytics";
import { cn } from "~/lib/utils";
import type { BillingBlockedPayload } from "~/features/billing/types";

interface BillingBlockedAlertDisplayProps {
    billing: BillingBlockedPayload;
    className?: string;
    title?: string;
    compact?: boolean;
}

interface BillingBlockedAlertProps extends BillingBlockedAlertDisplayProps {
    analyticsSurface?: string;
}

function getUnitsLabel(
    metricType: BillingBlockedPayload["metricType"],
): string {
    if (metricType === "client") return "clients";
    return "sessions";
}

// AIDEV-NOTE: metricType=null indicates practitioner-owned billing viewed from a client surface.
function getIsClientSurface(billing: BillingBlockedPayload): boolean {
    return billing.metricType === null;
}

function getBodyCopy(billing: BillingBlockedPayload): string {
    if (getIsClientSurface(billing)) {
        return "Your practitioner has reached their session limit for this period. Not to worry, your session has been saved, and will be automatically processed when your practitioner upgrades their account. Your practitioner has been notified.";
    }

    if (billing.thresholdState !== "blocked") {
        return `You've used ${billing.usedUnits ?? ""} of ${
            billing.limitUnits ?? ""
        } sessions included in your plan this period. Upgrade anytime to get more sessions.`;
    }

    if (billing.blockReason === "paid_plan_over_grace") {
        return "You've gone over your plan's session allowance for this period. New sessions are still being saved and will process once you upgrade.";
    }

    if (billing.blockReason === "trial_hard_stop") {
        return "You've used all the sessions included in your trial. Your sessions are safe — upgrade your plan to continue processing.";
    }

    return "You've reached the session limit for your plan. New sessions will continue to be accepted and saved — they'll be processed automatically once you upgrade.";
}

export function BillingBlockedAlertDisplay({
    billing,
    className,
    title,
    compact = false,
}: BillingBlockedAlertDisplayProps) {
    const isBlocked = billing.thresholdState === "blocked";
    const isClientSurface = getIsClientSurface(billing);

    return (
        <div
            role="alert"
            className={cn(
                "rounded-xl border px-4 py-3",
                isBlocked
                    ? "border-red-200 bg-red-50 text-red-900"
                    : "border-amber-200 bg-amber-50 text-amber-900",
                className,
            )}
        >
            <div className="flex items-center justify-between gap-4">
                <div className="flex-1 space-y-1">
                    <p className="text-sm font-semibold">
                        {title ||
                            (isClientSurface
                                ? "Session Processing Paused"
                                : isBlocked
                                  ? "Session processing is paused"
                                  : "You're approaching your session limit")}
                    </p>
                    <p className="text-sm">{getBodyCopy(billing)}</p>
                    {!isClientSurface && (
                        <div className="pt-2">
                            <PaywallContactDialog
                                surface="blocked_alert"
                                billingState={billing.thresholdState}
                                variant={isBlocked ? "red" : "amber"}
                                triggerIcon="arrow-right"
                            />
                        </div>
                    )}
                </div>
                {!isClientSurface &&
                    !compact &&
                    billing.usedUnits !== null &&
                    billing.limitUnits !== null && (
                        <p
                            className={cn(
                                "shrink-0 text-sm font-semibold tabular-nums",
                                isBlocked ? "text-red-700" : "text-amber-700",
                            )}
                        >
                            {billing.usedUnits}/{billing.limitUnits}
                        </p>
                    )}
            </div>
        </div>
    );
}

export function BillingBlockedAlert({
    billing,
    className,
    title,
    compact = false,
    analyticsSurface = "billing_blocked_alert",
}: BillingBlockedAlertProps) {
    const hasTracked = useRef(false);

    useEffect(() => {
        if (hasTracked.current) return;
        if (
            billing.thresholdState !== "warning_80" &&
            billing.thresholdState !== "warning_100" &&
            billing.thresholdState !== "blocked"
        ) {
            return;
        }

        hasTracked.current = true;
        trackEvent("billing_blocked_ui_shown", {
            surface: analyticsSurface,
            metricType: billing.metricType,
            thresholdState: billing.thresholdState,
            blockReason: billing.blockReason,
        });
    }, [
        analyticsSurface,
        billing.blockReason,
        billing.metricType,
        billing.thresholdState,
    ]);

    return (
        <BillingBlockedAlertDisplay
            billing={billing}
            className={className}
            title={title}
            compact={compact}
        />
    );
}
